/**
 * Panel showing all discovered workflow variables.
 *
 * @module FlowDesigner/components/WorkflowVariablesPanel
 *
 * Displayed in the Configure tab when no node is selected.
 * Shows a simple list of variables with source badges and tooltip details.
 * Includes a "Reset flow" button to clear all agent-derived data.
 */

import React from 'react';
import { Button, Dialog, DialogContent, DialogFooter, DialogTrigger, DocsAnnotation, Flex, Heading, Text, useToast } from '@chatsdet/ui';
import { ArrowCounterClockwise, SlidersHorizontal, Warning } from '@phosphor-icons/react';
import type { VariableSource, WorkflowInputDefinition, WorkflowScenario } from '@chatsdet/types';
import type { WorkflowVariable } from '../hooks/useWorkflowVariables';
import { useWorkflowVariablesContext } from '../context/WorkflowVariablesContext';
import { useResetWorkflow } from '../../../../hooks/useWorkflows';
import { useAuth } from '../../../../contexts/AuthContext';

const SOURCE_STYLES: Record<VariableSource, { label: string; className: string }> = {
  inputValue: { label: 'Input', className: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  macro: { label: 'Macro', className: 'bg-purple-500/15 text-purple-400 border-purple-500/30' },
  extracted: { label: 'Extracted', className: 'bg-green-500/15 text-green-400 border-green-500/30' },
  credential: { label: 'Credential', className: 'bg-orange-500/15 text-orange-400 border-orange-500/30' },
  seed: { label: 'Seed', className: 'bg-teal-500/15 text-teal-400 border-teal-500/30' },
  childFlowOutput: { label: 'Called Flow', className: 'bg-sky-500/15 text-sky-400 border-sky-500/30' },
  workflowInput: { label: 'Workflow Input', className: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30' },
  env: { label: 'Environment', className: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30' },
  unknown: { label: 'Variable', className: 'bg-muted text-muted-foreground border-border' },
};

interface WorkflowVariablesPanelProps {
  onNodeSelect?: (nodeId: string) => void;
  workflowId: string | null;
  workflowInputs?: WorkflowInputDefinition[];
  onSaveInputs?: (inputs: WorkflowInputDefinition[]) => void;
  isSavingInputs?: boolean;
  workflowScenarios?: WorkflowScenario[];
  onSaveScenarios?: (scenarios: WorkflowScenario[]) => void;
  isSavingScenarios?: boolean;
  onOpenScenarioConfig?: () => void;
}

function buildTooltip(variable: WorkflowVariable): string {
  const status = variable.hasDesignTimeValue ? 'Has value at design time' : 'Populated after run';
  const refs = variable.referencedByNodeIds.length;
  const refText = refs === 1 ? '1 node references this' : `${refs} nodes reference this`;

  if (variable.hasCollision) {
    const definerList = variable.allDefiners
      .map((d) => `Step ${d.stepIndex} (${d.stepLabel})`)
      .join(', ');
    return `${status}\n${refText}\n\nDefined by multiple steps: ${definerList}\nUse $var.StepN_Label.${variable.name} to disambiguate`;
  }

  return `${status}\n${refText}`;
}

export const WorkflowVariablesPanel: React.FC<WorkflowVariablesPanelProps> = ({
  onNodeSelect,
  workflowId,
  workflowInputs,
  onSaveInputs,
  isSavingInputs,
  workflowScenarios,
  onSaveScenarios,
  onOpenScenarioConfig,
}) => {
  const variables = useWorkflowVariablesContext();
  const resetMutation = useResetWorkflow(workflowId);
  const { toast } = useToast();
  const { user } = useAuth();
  const [isResetDialogOpen, setIsResetDialogOpen] = React.useState(false);
  const scenariosEnabled = user?.features?.workflowScenarios ?? false;

  const handleReset = async () => {
    try {
      await resetMutation.mutateAsync();
      toast.success('Flow reset. The next run will do a full agent execution.');
    } catch {
      toast.error('Could not reset the flow. Please try again.');
    } finally {
      setIsResetDialogOpen(false);
    }
  };

  const resetButton = workflowId ? (
    <div className="shrink-0 px-4 py-3 border-t border-border">
      <DialogTrigger isOpen={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <Button variant="outline" className="w-full gap-2" size="sm">
          <ArrowCounterClockwise size={14} />
          Reset flow
        </Button>
        <Dialog title="Reset flow?" description="This will clear all agent-derived data">
          <DialogContent>
            <Text size="sm">
              This will clear all cached browser actions, agent notes, and playbook steps.
              The next run will perform a full agent execution from scratch.
            </Text>
            <Text size="sm" className="text-muted-foreground mt-2">
              Your node structure, input values, and run history will be preserved.
            </Text>
          </DialogContent>
          <DialogFooter>
            <Flex gap="sm" justify="end">
              <Button variant="outline" onPress={() => setIsResetDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onPress={handleReset}
                isDisabled={resetMutation.isPending}
              >
                {resetMutation.isPending ? 'Resetting...' : 'Reset'}
              </Button>
            </Flex>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </div>
  ) : null;

  const scenarioConfigButton =
    scenariosEnabled && onSaveInputs && onSaveScenarios && onOpenScenarioConfig ? (
      <div className="shrink-0 border-b border-border px-4 py-3">
        <Flex align="center" justify="between" gap="sm">
          <div className="min-w-0">
            <Text size="xs" weight="medium" className="text-muted-foreground uppercase tracking-wide">
              Workflow Scenarios
            </Text>
            <Text size="xs" className="text-muted-foreground truncate">
              {(workflowInputs ?? []).length} input{(workflowInputs ?? []).length === 1 ? '' : 's'} ·{' '}
              {(workflowScenarios ?? []).length} scenario
              {(workflowScenarios ?? []).length === 1 ? '' : 's'}
            </Text>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onPress={onOpenScenarioConfig}
            isDisabled={isSavingInputs}
          >
            <SlidersHorizontal size={14} />
            Configure scenarios
          </Button>
        </Flex>
      </div>
    ) : null;

  if (variables.length === 0) {
    return (
      <div className="flex flex-col h-full min-h-0">
        {scenarioConfigButton}
        <div className="flex-1 flex items-center justify-center text-center px-6">
          <div>
            <Heading level={5} className="mb-2">
              Variables
            </Heading>
            <Text size="sm" className="text-muted-foreground">
              No variables yet. Variables are created when you add macros or input values to action
              nodes.
            </Text>
          </div>
        </div>
        {resetButton}
      </div>
    );
  }

  return (
    <DocsAnnotation slug="reference/macros" title="Macros & Variables">
    <div className="flex flex-col h-full min-h-0">
      {scenarioConfigButton}

      <div className="shrink-0 py-4 px-4 border-b border-border">
        <Heading level={5} className="mb-1">
          Variables
        </Heading>
        <Text size="sm" className="text-muted-foreground">
          {variables.length} variable{variables.length !== 1 ? 's' : ''} discovered across workflow
          nodes
        </Text>
      </div>

      <div className="flex-1 overflow-y-auto">
        {variables.map((v) => {
          const style = SOURCE_STYLES[v.source];
          const clickableNodeId = v.referencedByNodeIds[0] ?? v.definedByNodeId;

          return (
            <div
              key={v.name}
              title={buildTooltip(v)}
              className={`mx-2 px-2 py-2 rounded-md ${
                clickableNodeId && onNodeSelect
                  ? 'cursor-pointer hover:bg-muted/50'
                  : ''
              }`}
              onClick={() => {
                if (clickableNodeId && onNodeSelect) {
                  onNodeSelect(clickableNodeId);
                }
              }}
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-foreground truncate min-w-0">
                  $var.{v.name}
                </span>
                {v.hasCollision && (
                  <Warning
                    size={14}
                    weight="fill"
                    className="shrink-0 text-amber-400"
                  />
                )}
                <span
                  className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] border ${style.className}`}
                >
                  {style.label}
                </span>
              </div>
              {v.hasCollision ? (
                <div className="text-[11px] text-amber-400/80 mt-0.5">
                  Defined by {v.allDefiners.length} steps — use qualified name to disambiguate
                </div>
              ) : v.definedByStepLabel ? (
                <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                  Step {v.definedByStepIndex}: {v.definedByStepLabel}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {resetButton}
    </div>
    </DocsAnnotation>
  );
};
